import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import api from '../api';
import { Calendar, Flag, Users, Globe, Plus, X, Check, Lock, AlertTriangle, ChevronDown, ChevronUp, Repeat, ListTree, CornerDownRight, Paperclip, RotateCcw, UserPlus, Trash2, Send } from 'lucide-react';
import { Sheet, SearchBox, Seg, Avatar, plural } from './AdminUi';
import AddPeoplePanel from './AddPeoplePanel';
import AttachmentViewer from './AttachmentViewer';

// Jedan ekran za novi zadatak, podzadatak i uređivanje (admin i vlasnik grupe).
// Vlasnik grupe: vidi samo svoje grupe (+ tuđe koje zadatak već ima, zaključane), bar jedna njegova
// mora ostati označena; dodjeljuje bilo koga, ali ne može postaviti „obavezno”.
// Uređivanje: sudionici (dodaj, status rada, obavezno, ukloni, gosti) su nacrt do „Spremi promjene”;
// prihvaćanje/odbijanje javljenih ostaje u panelu Sudionici u detalju zadatka.
// Vlasnik grupe, novi zadatak: čip „Ostalo · prijedlog” pretvara ekran u prijedlog adminu
// (samo naziv, vrijeme, opis → POST /tasks/propose), pa mu ne treba zaseban gumb „Predloži”.
// mode="approve" (admin): prijedlog se otvara kao novi zadatak s već upisanim podacima → „Odobri i objavi”.
// mode="proposal" (predlagatelj): uređivanje vlastitog prijedloga dok čeka (naziv, vrijeme, opis).

// Naslov dobiva fokus samo uz miš (desktop); na mobitelu tipkovnica se otvara tek na dodir polja.
const FINE_POINTER = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: fine)').matches;
const pad = (n) => String(n).padStart(2, '0');
const toInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const fromIso = (s) => (s ? toInput(new Date(s)) : '');
const toIso = (v) => (v ? new Date(v).toISOString() : null);
const MAX_ATTACHMENTS = 3;
const ATT_NAME_MAX = 50;
const ATT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png';
const fileSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

function Switch({ on }) {
  return <span className={`tf-sw${on ? ' on' : ''}`} aria-hidden="true" />;
}

function SwitchRow({ icon, label, sub, on, onChange, disabled }) {
  return (
    <button type="button" className="tf-row tf-row-btn" onClick={() => !disabled && onChange(!on)} disabled={disabled} aria-pressed={on}>
      <span className="tf-ico">{icon}</span>
      <span className="tf-k">{label}{sub && <small>{sub}</small>}</span>
      <Switch on={on} />
    </button>
  );
}

// Odabir ljudi (samo novi zadatak): članovi odabranih grupa na vrhu, kvačice, „obavezno” samo admin.
function PeopleSheet({ users, groups, selectedGroups, picked, setPicked, isAdmin, me, onClose, z = 1310 }) {
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const match = (u) => !ql || (u.full_name || '').toLowerCase().includes(ql) || u.email.toLowerCase().includes(ql);
  const groupEmails = new Map();   // email → naziv prve odabrane grupe
  for (const g of groups) {
    if (!selectedGroups.includes(g.id)) continue;
    for (const m of g.members || []) if (!groupEmails.has(m.email)) groupEmails.set(m.email, `${g.name}${m.role === 'owner' ? ' · vlasnik' : ''}`);
  }
  const list = users.filter(match);
  const inGroups = list.filter(u => groupEmails.has(u.email));
  const others = list.filter(u => !groupEmails.has(u.email));
  const n = Object.keys(picked).length;
  const toggle = (email) => setPicked(p => { const x = { ...p }; if (x[email]) delete x[email]; else x[email] = { is_mandatory: false }; return x; });
  const toggleMand = (email) => setPicked(p => ({ ...p, [email]: { is_mandatory: !p[email]?.is_mandatory } }));
  const row = (u) => {
    const on = !!picked[u.email];
    return (
      <div key={u.email} className="adm-it" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
        onClick={() => toggle(u.email)} onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggle(u.email))}>
        <Avatar name={u.full_name || u.email} size={34} />
        <div className="adm-main">
          <div className="adm-t soft">{u.full_name || u.email}{u.email === me && <span className="me"> · ti</span>}</div>
          {groupEmails.has(u.email) && <div className="adm-s">{groupEmails.get(u.email)}</div>}
        </div>
        {on && isAdmin && (
          <button type="button" className={`tf-mand${picked[u.email].is_mandatory ? ' on' : ''}`}
            onClick={e => { e.stopPropagation(); toggleMand(u.email); }} title="Obavezno sudjelovanje — osoba ne može odbiti poziv">
            <AlertTriangle size={13} /> obavezno
          </button>
        )}
        <span className={`adm-check${on ? ' on' : ''}`}>{on && <Check size={15} strokeWidth={3} />}</span>
      </div>
    );
  };
  return (
    <Sheet tall z={z} onClose={onClose}
      head={<div className="adm-sh"><div className="adm-main"><h3>Dodaj ljude</h3><p>{n ? `${n} ${plural(n, 'odabran', 'odabrana', 'odabranih')}` : 'Odaberi jednog ili više ljudi.'}</p></div></div>}
      footer={<button type="button" className="adm-btn primary" onClick={onClose}>Gotovo</button>}>
      <SearchBox value={q} onChange={setQ} placeholder="Pretraži po imenu ili e-mailu" />
      {inGroups.length > 0 && <><div className="adm-lbl">Članovi odabranih grupa</div><div className="adm-card">{inGroups.map(row)}</div></>}
      {others.length > 0 && <><div className="adm-lbl">{inGroups.length ? 'Ostali' : 'Svi korisnici'}</div><div className="adm-card">{others.map(row)}</div></>}
      {list.length === 0 && <div className="adm-empty">Nitko ne odgovara pretrazi.</div>}
      {!isAdmin && <p className="adm-note">Obavezno sudjelovanje može postaviti samo admin.</p>}
    </Sheet>
  );
}

export default function TaskForm({ user, task = null, mode = null, parentTask = null, initialDate = null, onClose, onSaved, onSubtasksChanged, z = 1300 }) {
  const approveMode = mode === 'approve' && !!task;
  const proposalEdit = mode === 'proposal' && !!task;
  const isEdit = !!task && !approveMode && !proposalEdit;
  const isAdmin = user?.role === 'admin';
  const parent = parentTask || null;
  const isSub = !!parent || !!task?.parent_task_id;

  // ── Početne vrijednosti ──
  const init = useMemo(() => {
    if (task) {
      return {
        title: task.title || '', start: fromIso(task.start_date), end: fromIso(task.end_date),
        people: task.required_people || 1, isPublic: approveMode || task.is_public !== false,
        groups: (task.groups || []).map(g => g.id), cats: (task.categories || []).map(c => c.id),
        recurring: !!task.is_recurring, recurDay: task.recur_day || '', locked: approveMode || task.start_locked === true,   // prijedlog se objavljuje sa zadanim zaključavanjem
        container: task.is_container === true, subRequired: task.subtask_required !== false,
        parentId: task.parent_task_id || null,
      };
    }
    const base = initialDate ? initialDate.split('T')[0] : parent?.start_date ? fromIso(parent.start_date).split('T')[0] : '';
    return {
      title: '', start: base ? `${base}T08:00` : '', end: base ? `${base}T10:00` : '',
      people: 1, isPublic: true,
      groups: (parent?.groups || []).map(g => g.id), cats: (parent?.categories || []).map(c => c.id),
      recurring: false, recurDay: '', locked: true, container: false, subRequired: true, parentId: parent?.id || null,
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [title, setTitle] = useState(init.title);
  const [start, setStart] = useState(init.start);
  const [end, setEnd] = useState(init.end);
  const [people, setPeople] = useState(init.people);
  const [isPublic, setIsPublic] = useState(init.isPublic);
  const [selGroups, setSelGroups] = useState(init.groups);
  const [selCats, setSelCats] = useState(init.cats);
  const [recurring, setRecurring] = useState(init.recurring);
  const [recurDay, setRecurDay] = useState(init.recurDay);
  const [locked, setLocked] = useState(init.locked);
  const [container, setContainer] = useState(init.container);
  const [subRequired, setSubRequired] = useState(init.subRequired);
  const [parentId, setParentId] = useState(init.parentId);
  const [picked, setPicked] = useState({});           // novi zadatak: email → { is_mandatory }
  const [propose, setPropose] = useState(proposalEdit);      // vlasnik: „Ostalo · prijedlog” umjesto svoje grupe
  const [descDirty, setDescDirty] = useState(false);
  // Uređivanje: prilozi (brisanje i novi se primjenjuju na „Spremi”) i podzadaci (dodaju se svojim ekranom).
  const [attachments, setAttachments] = useState([]);
  const [removeAtt, setRemoveAtt] = useState([]);
  const [viewAtt, setViewAtt] = useState(null);     // postojeći prilog otvoren u AttachmentViewer
  const [newFiles, setNewFiles] = useState([]);       // [{ key, file, name }]
  const [subtasks, setSubtasks] = useState(task?.subtasks || []);
  const [subOpen, setSubOpen] = useState(false);
  // Uređivanje: sudionici (nacrt)
  const [volunteers, setVolunteers] = useState([]);
  const [taskGuests, setTaskGuests] = useState([]);
  const [allGuests, setAllGuests] = useState([]);
  const [pAdd, setPAdd] = useState([]);          // [{ email, is_mandatory, full_name, isVol }]
  const [gAdd, setGAdd] = useState([]);          // [guest]
  const [pRemove, setPRemove] = useState([]);    // [email]
  const [gRemove, setGRemove] = useState([]);    // [guest id]
  const [wsDraft, setWsDraft] = useState({});    // email → work_status
  const [mandDraft, setMandDraft] = useState({}); // email → is_mandatory
  const [openPerson, setOpenPerson] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const addListRef = useRef(null);

  const [groups, setGroups] = useState([]);
  const [cats, setCats] = useState([]);
  const [users, setUsers] = useState([]);
  const [parentCandidates, setParentCandidates] = useState(null);
  const [moreOpen, setMoreOpen] = useState(isEdit && (init.cats.length > 0 || !init.locked || init.container || init.recurring || !!init.parentId));
  const [peopleOpen, setPeopleOpen] = useState(false);
  const closeRef = useRef(null);
  const [upload, setUpload] = useState(null);         // { i, n, pct, key } dok se prilog učitava
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const editor = useEditor({
    extensions: [StarterKit],
    content: task?.description || '',
    onUpdate: () => setDescDirty(true),
  });

  useEffect(() => {
    api.get('/groups').then(r => setGroups(r.data)).catch(() => {});
    api.get('/categories').then(r => setCats(r.data)).catch(() => {});
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/tasks/${task.id}/attachments`).then(r => setAttachments(r.data)).catch(() => {});
      api.get(`/tasks/${task.id}/volunteers`).then(r => setVolunteers(r.data)).catch(() => {});
      api.get(`/tasks/${task.id}/guests`).then(r => setTaskGuests(r.data)).catch(() => {});
      api.get('/guests').then(r => setAllGuests(r.data)).catch(() => {});
    }
  }, [isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const ownedIds = useMemo(() => new Set(groups.filter(g => (g.members || []).some(m => m.email === user.email && m.role === 'owner')).map(g => g.id)), [groups, user.email]);

  // Vlasnik: novi zadatak bez grupe → označi njegovu prvu grupu.
  useEffect(() => {
    if (isAdmin || task || selGroups.length || !ownedIds.size) return;
    setSelGroups([groups.find(g => ownedIds.has(g.id)).id]);
  }, [ownedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleGroups = isAdmin ? groups : groups.filter(g => ownedIds.has(g.id) || init.groups.includes(g.id));
  const selOwned = selGroups.filter(id => ownedIds.has(id));
  const groupLocked = (id) => !isAdmin && (!ownedIds.has(id) || (selGroups.includes(id) && selOwned.length === 1));
  const toggleGroup = (id) => {
    if (propose) { setPropose(false); setSelGroups([id]); return; }
    if (groupLocked(id)) return;
    setSelGroups(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  };
  const canPropose = !isAdmin && !task && !parent && ownedIds.size > 0;
  const pickPropose = () => { if (!propose) { setPropose(true); setSelGroups([]); } };
  const toggleCat = (id) => setSelCats(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  const onStart = (v) => {
    if (v && start && end) {
      const gap = new Date(end) - new Date(start);
      if (gap >= 0) setEnd(toInput(new Date(new Date(v).getTime() + gap)));
    } else if (v && !end) {
      setEnd(toInput(new Date(new Date(v).getTime() + 3600000)));
    }
    setStart(v);
  };

  const pickedList = Object.entries(picked);
  const userName = (email) => { const u = users.find(x => x.email === email); return u?.full_name || email; };

  // Što nedostaje (tekst na gumbu Spremi).
  const missing = !title.trim() ? 'Upiši naziv'
    : !start ? 'Odaberi početak'
    : end && new Date(end) < new Date(start) ? 'Kraj je prije početka'
    : !isAdmin && !propose && selOwned.length === 0 ? 'Označi svoju grupu'
    : approveMode && !isPublic && !selGroups.length && !container ? 'Odaberi grupu ili vidljivo svima'
    : !propose && recurring && !(Number(recurDay) >= 1 && Number(recurDay) <= 31) ? 'Upiši dan ponavljanja'
    : null;

  const sameSet = (a, b) => a.length === b.length && a.every(x => b.includes(x));
  const dirty = title !== init.title || start !== init.start || end !== init.end || Number(people) !== Number(init.people)
    || isPublic !== init.isPublic || !sameSet(selGroups, init.groups) || !sameSet(selCats, init.cats)
    || recurring !== init.recurring || String(recurDay) !== String(init.recurDay) || locked !== init.locked
    || container !== init.container || subRequired !== init.subRequired || parentId !== init.parentId
    || pickedList.length > 0 || descDirty || removeAtt.length > 0 || newFiles.length > 0
    || pAdd.length > 0 || gAdd.length > 0 || pRemove.length > 0 || gRemove.length > 0
    || Object.keys(wsDraft).length > 0 || Object.keys(mandDraft).length > 0 || propose;


  const save = async () => {
    if (missing || busy) return;
    setBusy(true); setError('');
    const description = editor ? editor.getHTML() : (task?.description || '');
    try {
      if (proposalEdit) {
        await api.put(`/tasks/${task.id}/proposal`, { title: title.trim(), description, start_date: toIso(start), end_date: toIso(end) });
        onSaved && onSaved(task.id);
      } else if (propose) {
        const res = await api.post('/tasks/propose', { title: title.trim(), description, start_date: toIso(start), end_date: toIso(end) });
        onSaved && onSaved(res.data.id);
      } else if (approveMode) {
        await api.post(`/tasks/${task.id}/approve`, {
          title: title.trim(), description, start_date: toIso(start), end_date: toIso(end),
          is_public: isPublic, required_people: parseInt(people, 10) || 1,
          group_ids: selGroups, category_ids: selCats, start_locked: locked, is_container: container,
          assignments: container ? [] : pickedList.map(([email, v]) => ({ email, is_mandatory: v.is_mandatory })),
        });
        onSaved && onSaved(task.id);
      } else if (!isEdit) {
        const res = await api.post('/tasks', {
          title: title.trim(), description, start_date: toIso(start), end_date: toIso(end),
          is_recurring: recurring, recur_day: recurring ? parseInt(recurDay, 10) : null,
          is_public: isPublic, required_people: parseInt(people, 10) || 1,
          group_ids: selGroups, assignments: container ? [] : pickedList.map(([email, v]) => ({ email, is_mandatory: isAdmin && v.is_mandatory })),
          parent_task_id: parent?.id || null, subtask_required: subRequired,
          start_locked: locked, is_container: container,
        });
        if (selCats.length) await api.put(`/tasks/${res.data.id}/categories`, { category_ids: selCats });
        onSaved && onSaved(res.data.id);
      } else {
        await api.put(`/tasks/${task.id}`, {
          title: title.trim(), description, start_date: toIso(start), end_date: toIso(end),
          is_public: isPublic, start_locked: locked, is_container: container, required_people: parseInt(people, 10) || 1,
        });
        if (!sameSet(selGroups, init.groups)) await api.put(`/tasks/${task.id}/groups`, { group_ids: selGroups });
        if (!sameSet(selCats, init.cats)) await api.put(`/tasks/${task.id}/categories`, { category_ids: selCats });
        if (parentId !== init.parentId || subRequired !== init.subRequired) {
          await api.put(`/tasks/${task.id}/parent`, { parent_task_id: parentId, subtask_required: subRequired });
        }
        // Sudionici: dodavanja, uklanjanja, gosti, obavezno, status rada.
        for (const x of pAdd) await api.post(`/tasks/${task.id}/assignments`, { email: x.email, is_mandatory: isAdmin && !x.isVol && x.is_mandatory, action: 'add' });
        for (const email of pRemove) await api.post(`/tasks/${task.id}/assignments`, { email, action: 'remove' });
        for (const id of gRemove) await api.post(`/tasks/${task.id}/guests`, { guest_id: id, action: 'remove' });
        for (const g of gAdd) await api.post(`/tasks/${task.id}/guests`, { guest_id: g.id, action: 'add' });
        if (isAdmin) for (const [email, v] of Object.entries(mandDraft)) if (!pRemove.includes(email)) await api.put(`/tasks/${task.id}/assignments/${encodeURIComponent(email)}`, { is_mandatory: v });
        for (const [email, v] of Object.entries(wsDraft)) if (!pRemove.includes(email)) await api.put(`/tasks/${task.id}/work-status`, { work_status: v, email });
        for (const id of removeAtt) await api.delete(`/tasks/attachments/${id}`);
        for (let i = 0; i < newFiles.length; i++) {
          const f = newFiles[i];
          const fd = new FormData();
          fd.append('file', f.file);
          if (f.name.trim()) fd.append('name', f.name.trim().slice(0, ATT_NAME_MAX));
          await api.post(`/tasks/${task.id}/attachments`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => e.total && setUpload({ i: i + 1, n: newFiles.length, key: f.key, pct: Math.round((e.loaded / e.total) * 100) }),
          });
        }
        setUpload(null);
        onSaved && onSaved(task.id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Spremanje nije uspjelo.');
      setBusy(false); setUpload(null);
    }
  };

  const keptAtt = attachments.filter(a => !removeAtt.includes(a.id)).length;
  const canAddAtt = keptAtt + newFiles.length < MAX_ATTACHMENTS;
  const onPickFile = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setNewFiles(p => [...p, { key: `${Date.now()}${file.name}`, file, name: file.name.replace(/\.[^.]+$/, '').slice(0, ATT_NAME_MAX) }]);
  };
  const refreshSubtasks = async () => {
    try {
      const r = await api.get(`/tasks?year=${new Date(task.start_date).getFullYear()}`);
      const t = r.data.find(x => x.id === task.id);
      if (t) setSubtasks(t.subtasks || []);
    } catch { /* ignore */ }
    onSubtasksChanged && onSubtasksChanged();
  };

  const loadParents = async () => {
    try {
      const r = await api.get(`/tasks?year=${new Date(task.start_date).getFullYear()}`);
      // Isti kriterij kao dosadašnje uređivanje u detalju zadatka.
      setParentCandidates(r.data.filter(t => t.id !== task.id && !t.parent_task_id && !(t.subtasks?.length > 0)));
    } catch { setParentCandidates([]); }
  };

  const moreCount = selCats.length + (recurring ? 1 : 0) + (!locked ? 1 : 0) + (container ? 1 : 0) + (parentId && isEdit ? 1 : 0);
  const heading = isEdit ? 'Uredi zadatak' : approveMode ? 'Pregledaj prijedlog' : proposalEdit ? 'Uredi prijedlog'
    : parent ? 'Novi podzadatak' : propose ? 'Prijedlog zadatka' : 'Novi zadatak';
  const canBeContainer = !isSub && !(isEdit && (task.assignments || []).some(a => a.status !== 'declined'));

  return (
    <>
      <Sheet tall z={z} onClose={onClose} dirty={dirty} busy={busy} closeRef={closeRef}
        head={
          <div className="adm-sh">
            <div className="adm-main">
              <h3>{heading}</h3>
              {parent && <p>Dio zadatka: {parent.title}</p>}
              {approveMode && <p>Predlaže: {task.proposed_by_name || task.proposed_by}. Dopuni grupe i ljude pa objavi.</p>}
              {propose && <p>{proposalEdit ? 'Čeka odobrenje admina' : 'Ide adminu na odobrenje'}</p>}
            </div>
          </div>
        }
        footer={
          <div className="adm-dirty">
            {upload && (
              <div className="tf-prog" aria-live="polite">
                <span>Učitavanje priloga {upload.i}/{upload.n} · {upload.pct}%</span>
                <div className="tf-bar"><i style={{ width: `${upload.pct}%` }} /></div>
              </div>
            )}
            {error && <p style={{ color: '#ac463b' }}>{error}</p>}
            <div className="tf-foot">
              <button type="button" className="adm-btn" disabled={busy} onClick={() => closeRef.current && closeRef.current()}>
                Odustani
              </button>
              <button type="button" className={`adm-btn ${missing ? 'tf-off' : 'primary'}`} disabled={!!missing || busy} onClick={save}>
                {busy ? (upload ? 'Učitavam…' : 'Spremam…') : missing || (proposalEdit ? 'Spremi prijedlog' : approveMode ? 'Odobri i objavi' : propose ? 'Pošalji prijedlog' : isEdit ? 'Spremi promjene' : 'Spremi zadatak')}
              </button>
            </div>
          </div>
        }
      >
        <input id="tf-title" className="tf-title" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Naziv zadatka" maxLength={100} autoFocus={!isEdit && FINE_POINTER} />

        <div className="adm-lbl">Kada</div>
        <div className="adm-card">
          <label className="tf-row">
            <span className="tf-ico"><Calendar size={17} /></span>
            <span className="tf-k">Početak</span>
            <input id="tf-start" type="datetime-local" className="tf-dt" value={start} onChange={e => onStart(e.target.value)} />
          </label>
          <label className="tf-row">
            <span className="tf-ico"><Flag size={17} /></span>
            <span className="tf-k">Kraj</span>
            <input id="tf-end" type="datetime-local" className="tf-dt" value={end} onChange={e => setEnd(e.target.value)} />
          </label>
        </div>

        {!propose && <>
        <div className="adm-lbl">Ljudi</div>
        <div className="adm-card">
          {!container && (
            <div className="tf-row">
              <span className="tf-ico"><Users size={17} /></span>
              <span className="tf-k">Potrebno ljudi</span>
              <span className="tf-step">
                <button type="button" onClick={() => setPeople(p => Math.max(1, Number(p) - 1))} aria-label="Manje">−</button>
                <b>{people}</b>
                <button type="button" onClick={() => setPeople(p => Math.min(100, Number(p) + 1))} aria-label="Više">+</button>
              </span>
            </div>
          )}
          <SwitchRow icon={<Globe size={17} />} label="Vidljivo svima" on={isPublic} onChange={setIsPublic}
            sub={isPublic ? 'svi korisnici vide zadatak i mogu se javiti' : 'vide ga samo članovi odabranih grupa'} />
        </div>
        </>}

        {visibleGroups.length > 0 && !proposalEdit && (
          <>
            <div className="adm-lbl">{isAdmin ? 'Grupe' : visibleGroups.length === 1 ? 'Grupa' : 'Grupe'}</div>
            <div className="tf-chips">
              {visibleGroups.map(g => {
                const on = selGroups.includes(g.id);
                const lock = groupLocked(g.id);
                return (
                  <button key={g.id} type="button" className={`tf-chip${on ? ' on' : ''}${lock ? ' locked' : ''}`} onClick={() => toggleGroup(g.id)} aria-pressed={on}>
                    {lock && on && !propose ? <Lock size={13} /> : on ? <Check size={14} strokeWidth={3} /> : null} {g.name}
                  </button>
                );
              })}
              {canPropose && (
                <button type="button" className={`tf-chip${propose ? ' on' : ''}`} onClick={pickPropose} aria-pressed={propose}>
                  {propose ? <Check size={14} strokeWidth={3} /> : <Send size={13} />} Ostalo · prijedlog
                </button>
              )}
            </div>
            {propose ? (
              <p className="adm-note">Zadatak izvan tvojih grupa šalješ kao prijedlog. Admin ga odobrava ili odbija, a do tada ga vidiš samo ti.</p>
            ) : !isAdmin && (
              <p className="adm-note">
                {selOwned.length === 0 ? 'Označi bar jednu svoju grupu.' : 'Zadatak mora imati tvoju grupu.'}
                {selGroups.length > 0 && ' Članovi označenih grupa dobit će obavijest.'}
              </p>
            )}
          </>
        )}

        {!isEdit && !container && !propose && (
          <>
            <div className="adm-lbl">Dodijeljeni · {pickedList.length}</div>
            <div className="adm-card">
              {pickedList.map(([email, v]) => (
                <div key={email} className="adm-it">
                  <Avatar name={userName(email)} size={34} />
                  <div className="adm-main"><div className="adm-t soft">{userName(email)}</div></div>
                  {isAdmin && (
                    <button type="button" className={`tf-mand${v.is_mandatory ? ' on' : ''}`}
                      onClick={() => setPicked(p => ({ ...p, [email]: { is_mandatory: !p[email].is_mandatory } }))}>
                      <AlertTriangle size={13} /> obavezno
                    </button>
                  )}
                  <button type="button" className="adm-x" title="Makni" onClick={() => setPicked(p => { const x = { ...p }; delete x[email]; return x; })}><X size={18} /></button>
                </div>
              ))}
              <button type="button" className="adm-act" style={{ justifyContent: 'center' }} onClick={() => setPeopleOpen(true)}><Plus size={17} /> Dodaj ljude</button>
            </div>
            <p className="adm-note">
              {pickedList.length ? 'Dodijeljeni dobivaju poziv i mogu ga prihvatiti ili odbiti.' : 'Bez dodijeljenih: zadatak se objavljuje i ljudi se javljaju sami.'}
              {!isAdmin && ' Obavezno sudjelovanje može postaviti samo admin.'}
            </p>
          </>
        )}
        {isEdit && !container && (() => {
          const assigned = (task.assignments || []).filter(a => a.status !== 'declined');
          const accepted = assigned.filter(a => a.status === 'accepted');
          const invited = assigned.filter(a => a.status === 'pending');
          const WS = { pending: 'nije započeo/la', in_progress: 'u tijeku', done: 'gotovo' };
          const count = accepted.filter(a => !pRemove.includes(a.email)).length + taskGuests.filter(g => !gRemove.includes(g.id)).length
            + pAdd.filter(x => x.isVol).length + gAdd.length;
          const toggleDraft = (setter, email, val, orig) => setter(p => { const n = { ...p }; if (val === orig) delete n[email]; else n[email] = val; return n; });
          const row = (key, name, sub, { removed, isNewRow, onUndo, children, tag }) => {
            const open = openPerson === key;
            return (
              <React.Fragment key={key}>
                <div className={`adm-it${removed ? ' is-removed' : ''}${isNewRow ? ' is-new' : ''}`} role="button" tabIndex={0} style={{ cursor: removed ? 'default' : 'pointer' }}
                  onClick={() => !removed && setOpenPerson(open ? null : key)}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && !removed && (e.preventDefault(), setOpenPerson(open ? null : key))}>
                  <Avatar name={name} size={34} />
                  <div className="adm-main">
                    <div className="adm-t soft" style={{ fontSize: 16 }}>{name}</div>
                    {sub && <div className={sub.amber ? 'adm-pending' : 'adm-s'} style={sub.green ? { color: '#3f8f6b', fontWeight: 700 } : undefined}>{sub.text}</div>}
                  </div>
                  {removed
                    ? <button type="button" className="adm-pbtn" onClick={e => { e.stopPropagation(); onUndo(); }}><RotateCcw size={14} /> Vrati</button>
                    : <>{tag && <span className="tf-ptag">{tag}</span>}{open ? <ChevronUp size={18} className="adm-chev" /> : <ChevronDown size={18} className="adm-chev" />}</>}
                </div>
                {open && !removed && <div className="tf-pact">{children}</div>}
              </React.Fragment>
            );
          };
          const mandSwitch = (on, onToggle) => isAdmin && (
            <button type="button" className="tf-pswitch" onClick={onToggle}>
              <AlertTriangle size={16} color="#ac463b" /><span>Obavezno sudjelovanje</span><Switch on={on} />
            </button>
          );
          return (
            <>
              <div className="adm-lbl">Sudionici · {count}/{people}</div>
              <div className="adm-card">
                {accepted.map(a => {
                  const ws = wsDraft[a.email] ?? a.work_status ?? 'pending';
                  const mand = mandDraft[a.email] ?? !!a.is_mandatory;
                  const removed = pRemove.includes(a.email);
                  const changed = a.email in wsDraft || a.email in mandDraft;
                  return row(`a:${a.email}`, a.full_name || a.email,
                    removed ? { text: 'bit će uklonjen/a sa zadatka', amber: true }
                      : changed ? { text: `promjena: ${WS[ws]}${mand ? ' · obavezno' : ''}`, amber: true }
                      : { text: `${WS[ws]}${mand ? ' · obavezno' : ''}` },
                    { removed, onUndo: () => setPRemove(p => p.filter(x => x !== a.email)), tag: 'sudjeluje', children: (
                      <>
                        <Seg value={ws} onChange={v => toggleDraft(setWsDraft, a.email, v, a.work_status || 'pending')}
                          options={[{ value: 'pending', label: 'Čeka' }, { value: 'in_progress', label: 'U tijeku' }, { value: 'done', label: 'Gotovo' }]} />
                        {mandSwitch(mand, () => toggleDraft(setMandDraft, a.email, !mand, !!a.is_mandatory))}
                        <button type="button" className="adm-pbtn red" onClick={() => { setPRemove(p => [...p, a.email]); setOpenPerson(null); }}><Trash2 size={14} /> Ukloni sa zadatka</button>
                      </>
                    ) });
                })}
                {invited.map(a => {
                  const mand = mandDraft[a.email] ?? !!a.is_mandatory;
                  const removed = pRemove.includes(a.email);
                  return row(`i:${a.email}`, a.full_name || a.email,
                    removed ? { text: 'poziv će biti otkazan', amber: true } : { text: `poziv čeka odgovor${mand ? ' · obavezno' : ''}`, amber: a.email in mandDraft },
                    { removed, onUndo: () => setPRemove(p => p.filter(x => x !== a.email)), tag: 'poziv', children: (
                      <>
                        {mandSwitch(mand, () => toggleDraft(setMandDraft, a.email, !mand, !!a.is_mandatory))}
                        <button type="button" className="adm-pbtn red" onClick={() => { setPRemove(p => [...p, a.email]); setOpenPerson(null); }}><X size={14} /> Otkaži poziv</button>
                      </>
                    ) });
                })}
                {taskGuests.map(g => {
                  const removed = gRemove.includes(g.id);
                  return row(`g:${g.id}`, g.full_name, removed ? { text: 'gost će biti uklonjen', amber: true } : { text: 'gost' },
                    { removed, onUndo: () => setGRemove(p => p.filter(x => x !== g.id)), tag: 'gost', children: (
                      <button type="button" className="adm-pbtn red" onClick={() => { setGRemove(p => [...p, g.id]); setOpenPerson(null); }}><Trash2 size={14} /> Ukloni gosta</button>
                    ) });
                })}
                {pAdd.map(x => row(`n:${x.email}`, x.full_name || x.email,
                  { text: x.isVol ? 'novo · javio/la se, odmah sudjeluje' : `novo · dobit će poziv${x.is_mandatory ? ' · obavezno' : ''}`, green: true },
                  { isNewRow: true, children: (
                    <>
                      {!x.isVol && mandSwitch(x.is_mandatory, () => setPAdd(p => p.map(y => (y.email === x.email ? { ...y, is_mandatory: !y.is_mandatory } : y))))}
                      <button type="button" className="adm-pbtn red" onClick={() => { setPAdd(p => p.filter(y => y.email !== x.email)); setOpenPerson(null); }}><X size={14} /> Makni iz dodavanja</button>
                    </>
                  ) }))}
                {gAdd.map(g => row(`ng:${g.id}`, g.full_name, { text: 'novo · gost', green: true }, { isNewRow: true, children: (
                  <button type="button" className="adm-pbtn red" onClick={() => { setGAdd(p => p.filter(y => y.id !== g.id)); setOpenPerson(null); }}><X size={14} /> Makni iz dodavanja</button>
                ) }))}
                <button type="button" className="adm-act" style={{ justifyContent: 'center' }} onClick={() => setAddOpen(true)}><UserPlus size={17} /> Dodaj ljude</button>
              </div>
              <p className="adm-note">
                Dodir na osobu: status rada{isAdmin ? ', obavezno sudjelovanje' : ''} ili uklanjanje. Sve se primjenjuje tek na „Spremi promjene”.
                Javljene prihvaćaš ili odbijaš u detalju zadatka, u traci Sudionici.
              </p>
            </>
          );
        })()}

        <div className="adm-lbl">Opis</div>
        <div className="tiptap-editor tf-editor">
          <div className="tiptap-toolbar">
            <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor?.isActive('bold') ? 'is-active' : ''}>B</button>
            <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor?.isActive('italic') ? 'is-active' : ''}><i>I</i></button>
            <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor?.isActive('bulletList') ? 'is-active' : ''}>• Lista</button>
            <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor?.isActive('orderedList') ? 'is-active' : ''}>1. Lista</button>
          </div>
          <EditorContent editor={editor} />
        </div>

        {isEdit && (
          <>
            <div className="adm-lbl">Prilozi · {keptAtt + newFiles.length}/{MAX_ATTACHMENTS}</div>
            <div className="adm-card">
              {attachments.map(a => {
                const gone = removeAtt.includes(a.id);
                return (
                  <div key={a.id} className={`adm-it${gone ? ' is-removed' : ''}`}>
                    <span className="tf-ico"><Paperclip size={17} /></span>
                    <div className="adm-main" style={gone ? undefined : { cursor: 'pointer' }} onClick={gone ? undefined : () => setViewAtt(a)}>
                      <div className="adm-t soft" style={{ fontSize: 15.5 }}>{a.original_name}</div>
                      {gone ? <div className="adm-pending">bit će obrisan</div> : <div className="adm-s">{fileSize(a.file_size || 0)}</div>}
                    </div>
                    {gone
                      ? <button type="button" className="adm-pbtn" onClick={() => setRemoveAtt(p => p.filter(x => x !== a.id))}><RotateCcw size={14} /> Vrati</button>
                      : <button type="button" className="adm-x" title="Obriši prilog" disabled={busy} onClick={() => setRemoveAtt(p => [...p, a.id])}><X size={18} /></button>}
                  </div>
                );
              })}
              {newFiles.map(f => (
                <div key={f.key} className="adm-it">
                  <span className="tf-ico" style={{ color: '#3f8f6b' }}><Paperclip size={17} /></span>
                  <div className="adm-main">
                    <input className="adm-input" style={{ padding: '8px 10px', fontSize: 15 }} value={f.name} maxLength={ATT_NAME_MAX}
                      onChange={e => setNewFiles(p => p.map(x => (x.key === f.key ? { ...x, name: e.target.value } : x)))} placeholder="Naziv priloga" />
                    {upload?.key === f.key
                      ? <div className="tf-bar" style={{ marginTop: 6 }}><i style={{ width: `${upload.pct}%` }} /></div>
                      : <div className="adm-pending" style={{ color: '#3f8f6b' }}>novo · {fileSize(f.file.size)} · učitava se na „Spremi”</div>}
                  </div>
                  <button type="button" className="adm-x" title="Makni" disabled={busy} onClick={() => setNewFiles(p => p.filter(x => x.key !== f.key))}><X size={18} /></button>
                </div>
              ))}
              {canAddAtt ? (
                <label className="adm-act" style={{ justifyContent: 'center', cursor: 'pointer' }}>
                  <Plus size={17} /> Dodaj prilog
                  <input type="file" accept={ATT_ACCEPT} style={{ display: 'none' }} onChange={onPickFile} />
                </label>
              ) : (
                <div className="adm-act" style={{ justifyContent: 'center', color: '#9aa1ac', cursor: 'default' }}>Najviše {MAX_ATTACHMENTS} priloga</div>
              )}
            </div>

            {!task.parent_task_id && (
              <>
                <div className="adm-lbl">Podzadaci · {subtasks.length}</div>
                <div className="adm-card">
                  {[...subtasks].sort((a, b) => new Date(a.start_date) - new Date(b.start_date)).map(st => (
                    <div key={st.id} className="adm-it">
                      <span className="tf-ico"><CornerDownRight size={17} /></span>
                      <div className="adm-main">
                        <div className="adm-t soft" style={{ fontSize: 15.5 }}>{st.title}</div>
                        <div className="adm-s">{new Date(st.start_date).toLocaleDateString('hr-HR', { day: 'numeric', month: 'numeric' })}{st.subtask_required ? ' · obavezan za dovršetak' : ''}{st.status === 'done' ? ' · gotov' : ''}</div>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="adm-act" style={{ justifyContent: 'center' }} onClick={() => setSubOpen(true)}><Plus size={17} /> Dodaj podzadatak</button>
                </div>
                <p className="adm-note">Podzadatak se sprema odmah u svom ekranu; ostale promjene ovdje tek na „Spremi promjene”.</p>
              </>
            )}
          </>
        )}

        {!propose && <div className="adm-card" style={{ marginTop: 4 }}>
          <button type="button" className="tf-row tf-row-btn" onClick={() => setMoreOpen(o => !o)} aria-expanded={moreOpen}>
            <span className="tf-k" style={{ color: '#1a2a4a', fontWeight: 700 }}>
              Više opcija
              <small>{moreCount ? `${moreCount} ${plural(moreCount, 'uključena', 'uključene', 'uključenih')}` : 'kategorije, ponavljanje, raniji početak'}</small>
            </span>
            {moreOpen ? <ChevronUp size={20} className="adm-chev" /> : <ChevronDown size={20} className="adm-chev" />}
          </button>
        </div>}

        {moreOpen && !propose && (
          <>
            {cats.length > 0 && (
              <>
                <div className="adm-lbl">Kategorije</div>
                <div className="tf-chips">
                  {cats.map(c => (
                    <button key={c.id} type="button" className={`tf-chip${selCats.includes(c.id) ? ' on' : ''}`} onClick={() => toggleCat(c.id)} aria-pressed={selCats.includes(c.id)}>
                      {c.emoji} {c.name}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="adm-lbl">Postavke</div>
            <div className="adm-card">
              {!isEdit && !approveMode && (
                <SwitchRow icon={<Repeat size={17} />} label="Ponavlja se svaki mjesec" on={recurring} onChange={setRecurring}
                  sub={recurring ? 'isti dan u mjesecu' : 'novi zadatak svakog mjeseca'} />
              )}
              {!isEdit && !approveMode && recurring && (
                <label className="tf-row">
                  <span className="tf-ico" />
                  <span className="tf-k">Dan u mjesecu</span>
                  <input id="tf-recur" type="number" min="1" max="31" className="tf-num" value={recurDay} onChange={e => setRecurDay(e.target.value)} placeholder="1–31" />
                </label>
              )}
              {/* start_locked je zadano uključen; prekidač je iznimka („Dopusti raniji početak” = start_locked false). */}
              <SwitchRow icon={<Lock size={17} />} label="Dopusti raniji početak" on={!locked} onChange={v => setLocked(!v)}
                sub={locked ? 'prihvatiti se može odmah, započeti tek na dan početka' : 'može se započeti i prije dana početka'} />
              {canBeContainer && (
                <SwitchRow icon={<ListTree size={17} />} label="Povezani zadaci" on={container} onChange={setContainer}
                  sub="bez svojih sudionika, samo veže podzadatke" />
              )}
              {isSub && parentId && (
                <SwitchRow icon={<CornerDownRight size={17} />} label="Obavezan za dovršetak glavnog" on={subRequired} onChange={setSubRequired}
                  sub="glavni zadatak se ne zatvara dok ovaj nije gotov" />
              )}
            </div>

            {isEdit && !(task.subtasks?.length > 0) && !container && (
              <>
                <div className="adm-lbl">Glavni zadatak</div>
                {parentId ? (
                  <div className="adm-card">
                    <div className="tf-row">
                      <span className="tf-ico"><CornerDownRight size={17} /></span>
                      <span className="tf-k">{parentCandidates?.find(c => c.id === parentId)?.title || task.parent_task_title || `Zadatak #${parentId}`}</span>
                      <button type="button" className="adm-pbtn red" onClick={() => setParentId(null)}>Odvoji</button>
                    </div>
                  </div>
                ) : parentCandidates ? (
                  <select id="tf-parent" className="adm-input" defaultValue="" onChange={e => e.target.value && setParentId(parseInt(e.target.value, 10))}>
                    <option value="" disabled>Odaberi glavni zadatak…</option>
                    {parentCandidates.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                ) : (
                  <button type="button" className="adm-btn" onClick={loadParents}><CornerDownRight size={17} /> Poveži s glavnim zadatkom</button>
                )}
              </>
            )}
          </>
        )}
      </Sheet>

      {peopleOpen && (
        <PeopleSheet users={users} groups={groups} selectedGroups={selGroups} picked={picked} setPicked={setPicked}
          isAdmin={isAdmin} me={user.email} onClose={() => setPeopleOpen(false)} z={z + 10} />
      )}
      {addOpen && (
        <Sheet tall bare z={z + 10} onClose={() => setAddOpen(false)}
          head={<div className="adm-sh"><div className="adm-main"><h3>Dodaj na zadatak</h3><p>Dodani se primjenjuju na „Spremi promjene”.</p></div></div>}>
          <AddPeoplePanel
            task={task} user={user} allUsers={users} allGroups={groups} allGuests={allGuests} setAllGuests={setAllGuests}
            taskGuests={taskGuests} volunteers={volunteers} listRef={addListRef}
            excludeEmails={new Set(pAdd.map(x => x.email))} excludeGuestIds={new Set(gAdd.map(g => g.id))}
            onStage={({ members, guests }) => {
              setPAdd(p => [...p, ...members.filter(m => !p.some(x => x.email === m.email))]);
              setGAdd(p => [...p, ...guests.filter(g => !p.some(x => x.id === g.id))]);
              setAddOpen(false);
            }}
            onDone={() => setAddOpen(false)}
          />
        </Sheet>
      )}
      {subOpen && (
        <TaskForm user={user} parentTask={task} z={z + 20}
          onClose={() => setSubOpen(false)}
          onSaved={async () => { setSubOpen(false); await refreshSubtasks(); }} />
      )}
      {viewAtt && <AttachmentViewer attachment={viewAtt} onClose={() => setViewAtt(null)} />}
    </>
  );
}
